import { useDialog } from '@globals/dialog/useDialogStore'
import theme, { colors } from '@globals/themes/defaultTheme'
import { spacing, tabletQuery } from '@globals/themes/themeConstants'
import Close from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button, { ButtonProps } from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useT } from '@transifex/react'
import { PropsWithChildren, useState } from 'react'
import BottomDrawer from './BottomDrawer'

const confirmDialogTitleStyle: SxProps = {
  borderBottomWidth: 2,
  borderBottomColor: colors.blueExtraLight,
  borderBottomStyle: 'solid',
  paddingRight: spacing(4),
  paddingLeft: spacing(2),
}
export const confirmDialogCloseButtonStyle: SxProps = {
  position: 'absolute',
  right: 8,
  top: 8,
  color: colors.grayMedium,
}
const confirmDialogActionsStyle: SxProps = {
  padding: spacing(2),
  pt: 0,
}
const noMarginsStyle: SxProps = {
  margin: 0,
}

type ConfirmDialogProps = PropsWithChildren & {
  dialogKey: string
  cancelButtonText: React.ReactNode
  confirmButtonText: React.ReactNode
  onConfirm: () => void
  onClose?: () => void
  onCancel?: () => void
  disableConfirm?: boolean
  title: React.ReactNode
  variant?: 'primary' | 'danger'
  cancelButtonColor?: ButtonProps['color']
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = props => {
  const {
    dialogKey,
    cancelButtonText,
    confirmButtonText,
    title,
    onClose,
    onConfirm,
    onCancel,
    disableConfirm = false,
    variant = 'primary',
    children,
    cancelButtonColor,
  } = props
  const [isPendingClick, setIsPendingClick] = useState(false)
  const isTablet = useMediaQuery(tabletQuery)
  const { closeDialog, isOpen } = useDialog(dialogKey)

  const t = useT()

  const handleCloseDialog = () => {
    if (onClose) {
      onClose()
    }
    closeDialog()
  }

  const handleOnConfirm = async () => {
    if (onConfirm) {
      try {
        setIsPendingClick(true)
        await onConfirm()
      } finally {
        setIsPendingClick(false)
      }
    }
  }

  const Actions = () => (
    <DialogActions sx={confirmDialogActionsStyle}>
      <Button
        id="btn-dialog-cancel"
        data-testid="confirm-dialog-cancel-btn"
        variant="outlined"
        color={cancelButtonColor || 'primary'}
        sx={noMarginsStyle}
        fullWidth={isTablet}
        onClick={!!onCancel ? onCancel : handleCloseDialog}>
        {cancelButtonText}
      </Button>
      <Button
        id="btn-dialog-confirm"
        data-testid="confirm-dialog-confirm-btn"
        variant="contained"
        fullWidth={isTablet}
        color={variant === 'danger' ? 'secondary' : 'primary'}
        sx={noMarginsStyle}
        onClick={handleOnConfirm}
        disabled={disableConfirm || isPendingClick}>
        {confirmButtonText}
      </Button>
    </DialogActions>
  )

  if (isTablet) {
    return (
      <BottomDrawer
        drawerKey={dialogKey}
        onClose={handleCloseDialog}
        title={title}
        drawerStyles={{ zIndex: theme.zIndex.modal + 1 }}
        backgroundColor={colors.white}>
        {!!children ? <Box sx={{ mb: 2 }}>{children}</Box> : null}
        <Actions />
      </BottomDrawer>
    )
  }

  return (
    <Dialog
      id={`dialog-confirm-${dialogKey}`}
      open={isOpen}
      scroll="paper"
      fullWidth
      onClose={handleCloseDialog}
      aria-labelledby="confirm-dialog"
      data-testid="confirm-dialog">
      <DialogTitle
        component="div"
        sx={confirmDialogTitleStyle}
        data-testid="confirm-dialog-content">
        <Typography variant="subtitle1" sx={{ mt: 0 }}>
          {title}
        </Typography>
      </DialogTitle>
      <Button
        id="btn-dialog-close"
        aria-label={t('Close')}
        onClick={handleCloseDialog}
        sx={confirmDialogCloseButtonStyle}>
        <Close sx={{ mt: 1, mr: 1 }} />
      </Button>
      <DialogContent sx={{ p: spacing(2) }}>{children}</DialogContent>
      <Actions />
    </Dialog>
  )
}
